// ============================================================
// Platform Control · Audit Logs — operator entry into the
// OLIVIUM audit trail. Reuses useAuditFeed() so this view is
// real, not stubbed. Renders a compact strip + a "go to OLIVIUM"
// link rather than re-implementing the full trust console feed.
// ============================================================

import { useNavigate } from 'react-router-dom'
import { useAuditFeed } from '../../../hooks/useAuditFeed'
import ViewShell, { Card, Section } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS, text } from '../../../shell/styles'

export default function AuditLogsView() {
  const navigate = useNavigate()
  const { events, stats, loading } = useAuditFeed()
  const recent = events.slice(0, 8)

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · AUDIT LOGS"
      title="Audit Logs"
      description="Every privileged action is recorded in olv_audit_trail with a hash chain. This view summarizes; OLIVIUM owns the full feed."
      actions={
        <button
          type="button"
          onClick={() => navigate('/square/olivium/feed')}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: `1px solid ${SHELL_TOKENS.brandLine}`,
            background: SHELL_TOKENS.brandSoft,
            color: SHELL_TOKENS.brand,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
          }}
        >
          Open Signal →
        </button>
      }
    >
      <Section label="LAST 24H">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 10,
          }}
        >
          <StatTile label="Total"     value={stats.total} />
          <StatTile label="Critical"  value={stats.criticals} tone={SHELL_TOKENS.red} />
          <StatTile label="Warnings"  value={stats.warnings}  tone={SHELL_TOKENS.amber} />
          <StatTile label="Denials"   value={stats.denials} />
        </div>
      </Section>

      <Section label="RECENT EVENTS">
        {loading ? (
          <EmptyState title="Loading" body="Hydrating the audit trail." />
        ) : recent.length === 0 ? (
          <EmptyState title="No events" body="Quiet on the trust layer." />
        ) : (
          <Card>
            {recent.map((e, i) => (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderBottom:
                    i === recent.length - 1
                      ? 'none'
                      : `1px solid ${SHELL_TOKENS.border}`,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                }}
              >
                <span style={{ color: SHELL_TOKENS.ink }}>
                  {e.event_type || e.action || 'event'}
                </span>
                <span style={{ color: SHELL_TOKENS.inkMuted }}>
                  {e.actor_id ?? 'system'} · {e.outcome ?? '—'}
                </span>
              </div>
            ))}
          </Card>
        )}
      </Section>
    </ViewShell>
  )
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: string
}) {
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 10,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${SHELL_TOKENS.border}`,
      }}
    >
      <div
        style={{
          ...text.kicker,
          fontSize: 8,
          letterSpacing: '0.18em',
          color: SHELL_TOKENS.inkFaint,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: tone || SHELL_TOKENS.ink,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {value}
      </div>
    </div>
  )
}
